import React, { useState } from 'react';
import {
  Modal,
  Button,
  Box,
  SpaceBetween,
  Form,
  FormField,
  Input,
  Textarea,
  Select,
  DatePicker,
  Alert,
  ColumnLayout
} from '@cloudscape-design/components';
import TimeInput from "@cloudscape-design/components/time-input";

const TaskCreateModal = ({ visible, onDismiss, onSubmit, member, currentUser }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [taskData, setTaskData] = useState({
    title: '',
    description: '',
    type: 'task',
    task_date: new Date().toISOString().split('T')[0], // 默认今天
    start_time: '09:00',
    end_time: '10:00',
    points: 10,
    category: 'household',
    member_id: member?.id || '',
    member_name: member?.name || '',
    member_role: member?.role || '',
    publisher_id: currentUser?.id || '',
    publisher_name: currentUser?.name || '',
    creator_id: currentUser?.id || '',
    family_id: currentUser?.family_id || '6869d0771dfa97eaefc3bbf7'
  });

  // 当member改变时更新表单
  React.useEffect(() => {
    if (member) {
      setTaskData(prev => ({
        ...prev,
        member_id: member.id,
        member_name: member.name,
        member_role: member.role
      }));
    }
  }, [member]);

  const handleInputChange = (field, value) => {
    setTaskData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!taskData.title.trim()) {
      setError('请输入任务标题');
      return;
    }

    if (!taskData.task_date) {
      setError('请选择任务日期');
      return;
    }

    if (!taskData.start_time) {
      setError('请选择开始时间');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // 构建完整的日期时间字符串
      const startDateTime = `${taskData.task_date}T${taskData.start_time}:00`;
      const endDateTime = taskData.end_time ? `${taskData.task_date}T${taskData.end_time}:00` : '';
      
      // 构建提交数据
      const submitData = {
        ...taskData,
        start_time: startDateTime,
        end_time: endDateTime,
        points: parseInt(taskData.points) || 0
      };
      
      // 移除临时字段
      delete submitData.task_date;

      await onSubmit(submitData);
      
      // 重置表单
      setTaskData({
        title: '',
        description: '',
        type: 'task',
        task_date: new Date().toISOString().split('T')[0],
        start_time: '09:00',
        end_time: '10:00',
        points: 10,
        category: 'household',
        member_id: member?.id || '',
        member_name: member?.name || '',
        member_role: member?.role || '',
        publisher_id: currentUser?.id || '',
        publisher_name: currentUser?.name || '',
        creator_id: currentUser?.id || '',
        family_id: currentUser?.family_id || '6869d0771dfa97eaefc3bbf7'
      });
      
      onDismiss();
    } catch (error) {
      setError(error.message || '创建任务失败');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    onDismiss();
  };

  const typeOptions = [
    { label: '任务', value: 'task' },
    { label: '计划', value: 'plan' },
    { label: '笔记', value: 'note' }
  ];
  
  return (
    <Modal
      onDismiss={handleClose}
      visible={visible}
      size="medium"
      header={`为 ${member?.name || '成员'} 创建任务`}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button key="cancel" variant="link" onClick={handleClose}>
              取消
            </Button>
            <Button 
              key="submit"
              variant="primary" 
              onClick={handleSubmit}
              loading={loading}
            >
              创建任务
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <Form>
        <SpaceBetween size="m">
          {error && (
            <Alert key="error" type="error" dismissible onDismiss={() => setError('')}>
              {error}
            </Alert>
          )}

          <FormField key="title" label="任务标题">
            <Input
              value={taskData.title}
              onChange={({ detail }) => handleInputChange('title', detail.value)}
              placeholder="输入任务标题"
            />
          </FormField>

          <FormField key="type" label="任务类型">
            <Select
              selectedOption={typeOptions.find(opt => opt.value === taskData.type)}
              onChange={({ detail }) => handleInputChange('type', detail.selectedOption.value)}
              options={typeOptions}
            />
          </FormField>

          <FormField key="description" label="任务描述">
            <Textarea
              value={taskData.description}
              onChange={({ detail }) => handleInputChange('description', detail.value)}
              placeholder="输入任务描述（可选）"
              rows={3}
            />
          </FormField>

          <FormField key="points" label="奖励金币">
            <Input
              type="number"
              value={taskData.points.toString()}
              onChange={({ detail }) => handleInputChange('points', parseInt(detail.value) || 0)}
              placeholder="1"
              min="0"
              invalid={taskData.points < 1 || taskData.points > 200}
            />
          </FormField>

          <FormField key="date" label="任务日期">
            <DatePicker
              value={taskData.task_date}
              onChange={({ detail }) => handleInputChange('task_date', detail.value)}
              placeholder="选择日期"
            />
          </FormField>

          <ColumnLayout key="time" columns={2}>
            <FormField label="开始时间">
              <TimeInput
                value={taskData.start_time}
                onChange={({ detail }) => handleInputChange('start_time', detail.value)}
                format="hh:mm"
                placeholder="09:00"
              />
            </FormField>

            <FormField label="结束时间">
              <TimeInput
                value={taskData.end_time}
                onChange={({ detail }) => handleInputChange('end_time', detail.value)}
                format="hh:mm"
                placeholder="10:00"
              />
            </FormField>
          </ColumnLayout>
        </SpaceBetween>
      </Form>
    </Modal>
  );
};

export default TaskCreateModal;